import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useService } from 'react-service-locator';
import generatePicker from 'antd/es/date-picker/generatePicker';
import c from 'classnames';
import dayJsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import 'antd/es/calendar/style';
import { up } from 'styled-breakpoints';
import styled, { createGlobalStyle } from 'styled-components';
import { dayjs, Dayjs } from '@lgg/isomorphic/dayjs';
import { Icon } from 'src/components/general/icon';
import { CalendarDatePicker } from 'src/components/general/inputs/calendar-date-picker';
import { NoScrollOverlay } from 'src/components/general/inputs/shared';
import { LggModal } from 'src/components/general/modals/lgg-modal';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useVisible } from 'src/hooks/use-visible';
import { AuthorizationService } from 'src/services/authorization.service';
import {
  inputFocusBorder,
  inputPlaceholderStyle,
  labelTextStyle,
} from 'src/theme/sub-themes/inputs-theme';

const BaseDatePicker = generatePicker<Dayjs>(dayJsGenerateConfig);

const PickPanelStyle = createGlobalStyle`
  .lgg-date-picker-dropdown {
    .ant-picker-panel-container {
      border-radius: 4px;

      .ant-picker-calendar {
        margin: 0;
        border: none;
        border-radius: 4px;
        box-shadow: 0 20px 40px 0 rgba(91, 101, 112, 0.1);
        border: solid 1px ${({ theme }) => theme.colors.koala};
        overflow: hidden;
      }
    }
  }
`;

const ModalDatePickerModal = styled(LggModal)`
  height: max-content;
  margin: 0 auto;
  max-width: max-content;
  padding-bottom: 0;

  .ant-modal-close {
    display: none;
  }
`;

const DatePickerContainer = styled.div`
  width: 100%;
`;

const Label = styled.span`
  ${labelTextStyle}
`;

export const LggDatePicker = styled(BaseDatePicker)`
  height: 40px;
  color: ${({ theme }) => theme.colors.smalt};
  border-radius: 4px;
  border: solid 1px ${({ theme }) => theme.colors.koala};
  width: 100%;
  box-shadow: none;

  input {
    color: ${({ theme }) => theme.colors.smalt};
    font-size: 13px;

    &::placeholder {
      ${inputPlaceholderStyle};
    }
  }

  &.ant-picker-focused,
  &.ant-picker-focused:hover {
    ${inputFocusBorder}
  }

  &:hover {
    border: solid 1px ${({ theme }) => theme.colors.koala};
  }

  &.ant-picker-disabled {
    border: solid 1px ${({ theme }) => theme.colors.koala};
    background: ${({ theme }) => theme.colors.porcelain};
    color: ${({ theme }) => theme.colors.geyser};

    input,
    svg {
      opacity: 50%;
    }
  }

  &.input-error {
    ${({ theme }) => theme.inputsTheme.errorBorder};
  }
`;

const StyledSuffixIcon = styled(Icon)`
  svg path {
    fill: ${({ theme }) => theme.colors.casper};
  }
`;

type Props = {
  onChange: (date: Date) => void;
  value?: Date;
  baseValue?: Date;
  label?: string;
  disabled?: boolean;
  format?: string;
  placeHolder?: string;
  hasError?: boolean;
  disabledDate?: (date: Dayjs) => boolean;
  updateOnPageChange?: boolean;
  allowClear?: boolean;
  showSuffixIcon?: boolean;
  readOnly?: boolean;
};

export const DatePicker = ({
  onChange,
  value,
  baseValue,
  label,
  disabled = false,
  format = 'YYYY-MM-DD hh:mm A',
  placeHolder,
  hasError,
  disabledDate,
  updateOnPageChange,
  allowClear = true,
  showSuffixIcon = true,
  readOnly = false,
  ...rest
}: Props) => {
  const panelVisibilityHandler = useVisible();
  const { t } = useTranslation(['common']);
  const breakpointUpMd = useBreakpoint(up('md'));
  const authState = useService(AuthorizationService);
  const companyTimezone = authState.getTimezone();

  const calendarDatePicker = useMemo(
    () => (
      <CalendarDatePicker
        variant="small"
        setDatePickerDate={(date) => {
          onChange(date);
        }}
        pickerBaseValue={baseValue}
        isPanelVisible={panelVisibilityHandler.visible}
        datePickerDate={value}
        disabledDate={disabledDate}
        updateOnPageChange={updateOnPageChange}
        onSelect={(date, mode) => {
          onChange(date);

          if (mode === 'month') {
            panelVisibilityHandler.close();
          }
        }}
      />
    ),
    [
      baseValue,
      disabledDate,
      onChange,
      panelVisibilityHandler,
      updateOnPageChange,
      value,
    ],
  );

  return (
    <DatePickerContainer className="lgg-date-picker" {...rest}>
      <Label className="date-picker-label">{label}</Label>
      <PickPanelStyle />
      {panelVisibilityHandler.visible && <NoScrollOverlay />}
      <LggDatePicker
        getPopupContainer={(targetElement) =>
          targetElement.parentElement ?? targetElement
        }
        value={value ? dayjs(value).tz(companyTimezone) : undefined}
        className={c({
          'input-error': hasError,
          'lgg-date-picker-input': true,
        })}
        disabled={disabled}
        format={format}
        placeholder={placeHolder ?? t('common:inputPlaceholder.date')}
        open={panelVisibilityHandler.visible}
        onFocus={() => {
          if (breakpointUpMd) panelVisibilityHandler.show();
        }}
        onClick={() => panelVisibilityHandler.show()}
        onBlur={() => {
          if (breakpointUpMd) panelVisibilityHandler.close();
        }}
        suffixIcon={showSuffixIcon ? <StyledSuffixIcon type="calendarNoPadding" /> : null}
        allowClear={allowClear}
        onChange={(date) => {
          if (date) {
            onChange(date.toDate());
          }
        }}
        dropdownClassName="lgg-date-picker-dropdown"
        panelRender={() => (breakpointUpMd ? calendarDatePicker : null)}
        inputReadOnly={readOnly}
      />
      {breakpointUpMd ? null : (
        <ModalDatePickerModal
          visible={panelVisibilityHandler.visible}
          onClose={panelVisibilityHandler.close}
        >
          {calendarDatePicker}
        </ModalDatePickerModal>
      )}
    </DatePickerContainer>
  );
};
